import type { OwnProfile, Prompt } from '@/lib/api';
import { photoIdFromPath } from '@/lib/photo-path';

// The one owner of PATCH /me/profile payload assembly. That endpoint is
// FULL-REPLACE (backend profile.go's UpdateProfileHandler feeds every
// body field straight into domain.NewProfile -- no partial-merge
// semantics), which is why this module exists: every profile screen must
// send fields owned by the OTHER screens too, and assembling that
// per-screen is exactly how the documented data-loss bug happened
// (profile/index.tsx's own header comment: editing your handle once
// wiped photos, issue #136's review). One pure function, one merge
// table, tested below profile-draft.test.ts.
//
// `current` is the raw GET /me/profile response shape -- photos as
// "/photos/{id}" paths (backend toPhotoURLs). The mapping back to
// stored ids happens HERE, once, so no screen can forget it: sending
// the GET shape back verbatim stores the literal path as a photo id
// and the next GET double-prefixes to "/photos//photos/{id}" (live bug
// found in profile-prompts' own save while extracting this module).
// photoIdFromPath is a no-op on values that are already stored ids, so
// callers that hold ids directly (profile-photos' photoIds state) pass
// through unchanged.
export type ProfileDraft = {
  handle?: string;
  lat?: number;
  lng?: number;
  radiusM?: number;
  bio?: string;
  prompts?: Prompt[];
  interests?: string[];
  photos?: string[];
  gender?: string;
  ageRange?: string;
  hideDemographics?: boolean;
};

// The complete full-replace body -- every field required, because under
// full-replace an omitted field is a wiped field. (api.updateProfile
// still carries its own ?? defaults for the empty case; a payload from
// here never triggers them, which is what fixed the demographics wipe:
// photos/prompts saves used to omit gender/ageRange/hideDemographics
// and silently blank them on the server.)
export type ProfilePayload = {
  handle: string;
  lat: number;
  lng: number;
  radiusM: number;
  bio: string;
  prompts: Prompt[];
  interests: string[];
  photos: string[];
  gender: string;
  ageRange: string;
  hideDemographics: boolean;
};

// Merge semantics: fields present in `change` win; everything absent
// rides through from `current` untouched. On create there is no
// `current`, and the payload is the whole draft with empty defaults for
// whatever the draft doesn't set (the same empty defaults a first-time
// profile legitimately wants, never a silent fallback for an edit --
// see api.updateProfile's own doc comment).
export function buildProfilePayload(current: OwnProfile | undefined, change: ProfileDraft): ProfilePayload {
  return {
    handle: change.handle ?? current?.handle ?? '',
    lat: change.lat ?? current?.lat ?? 0,
    lng: change.lng ?? current?.lng ?? 0,
    radiusM: change.radiusM ?? current?.radiusM ?? 0,
    bio: change.bio ?? current?.bio ?? '',
    prompts: change.prompts ?? current?.prompts ?? [],
    interests: change.interests ?? current?.interests ?? [],
    photos: (change.photos ?? current?.photos ?? []).map(photoIdFromPath),
    gender: change.gender ?? current?.gender ?? '',
    ageRange: change.ageRange ?? current?.ageRange ?? '',
    hideDemographics: change.hideDemographics ?? current?.hideDemographics ?? false,
  };
}
